import React, { createContext, useState, useContext, useEffect } from 'react';

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const translations = {
  ar: {
    // Header
    home: 'الرئيسية',
    products: 'المنتجات',
    cart: 'السلة',
    login: 'تسجيل الدخول',
    signup: 'إنشاء حساب',
    logout: 'تسجيل الخروج',
    profile: 'الحساب',
    myOrders: 'الطلبات',
    adminDashboard: 'لوحة التحكم',

    // Home
    heroTitle: 'اكتشف منتجات مميزة',
    heroSubtitle: 'تسوق أحدث المنتجات مع توصيل سريع لجميع الإمارات',
    shopNow: 'تسوق الآن',
    fastDelivery: 'توصيل سريع',
    fastDeliveryDesc: 'توصيل لجميع الإمارات - 30 درهم',
    securePayment: 'دفع آمن',
    securePaymentDesc: 'معاملات مشفرة 100%',
    easyReturns: 'إرجاع سهل',
    easyReturnsDesc: 'سياسة إرجاع 14 يوم',
    support: 'دعم على مدار الساعة',
    supportDesc: 'فريق دعم متخصص',
    featuredProducts: 'منتجات مميزة',
    viewAll: 'عرض الكل',
    newsletter: 'اشترك في النشرة البريدية',
    newsletterDesc: 'احصل على أحدث العروض والتحديثات',
    subscribe: 'اشترك',
    enterEmail: 'أدخل بريدك الإلكتروني',

    // Products
    allProducts: 'جميع المنتجات',
    discoverCollection: 'اكتشف مجموعتنا المميزة',
    search: 'بحث',
    searchProducts: 'ابحث عن المنتجات...',
    category: 'الفئة',
    allCategories: 'جميع الفئات',
    priceRange: 'نطاق السعر',
    min: 'الحد الأدنى',
    max: 'الحد الأقصى',
    clearFilters: 'مسح الفلاتر',
    productsFound: 'منتج',
    noProducts: 'لا توجد منتجات مطابقة',
    addToCart: 'أضف للسلة',
    featured: 'مميز',

    // Product Detail
    description: 'الوصف',
    inStock: 'متوفر',
    outOfStock: 'غير متوفر',
    available: 'متاح',
    quantity: 'الكمية',
    addedToCart: 'تمت الإضافة للسلة',
    deliveryNote: 'توصيل لجميع الإمارات - 30 درهم',
    glutenFree: 'خالي من الغلوتين 100%',

    // Cart
    shoppingCart: 'سلة التسوق',
    itemsInCart: 'منتج في السلة',
    emptyCart: 'سلة التسوق فارغة',
    emptyCartDesc: 'لم تقم بإضافة أي منتجات بعد',
    startShopping: 'ابدأ التسوق',
    orderSummary: 'ملخص الطلب',
    subtotal: 'المجموع الفرعي',
    shipping: 'التوصيل',
    tax: 'الضريبة',
    total: 'الإجمالي',
    free: 'مجاني',
    proceedToCheckout: 'متابعة الدفع',
    continueShopping: 'متابعة التسوق',

    // Checkout
    checkout: 'الدفع',
    shippingAddress: 'عنوان التوصيل',
    payment: 'الدفع',
    fullName: 'الاسم الكامل',
    address: 'العنوان',
    city: 'المدينة',
    emirate: 'الإمارة',
    selectEmirate: 'اختر الإمارة',
    phone: 'رقم الهاتف',
    cashOnDelivery: 'الدفع عند الاستلام',
    placeOrder: 'تأكيد الطلب',
    orderFailed: 'فشل في إنشاء الطلب',
    shippingTo: 'التوصيل إلى',
    edit: 'تعديل',
    paymentInfo: 'معلومات الدفع',
    paymentNote: 'للتجربة: استخدم بطاقة 4242 4242 4242 4242',
    pay: 'ادفع',
    processing: 'جاري المعالجة...',
    paymentMethod: 'طريقة الدفع',
    creditCard: 'بطاقة ائتمان / خصم',
    cardDetails: 'تفاصيل البطاقة',
    orPayWithCard: 'أو ادفع بالبطاقة',
    payNow: 'ادفع الآن',
    payWhenReceive: 'ادفع عند استلام طلبك',

    // Orders
    orders: 'الطلبات',
    viewTrackOrders: 'عرض وتتبع طلباتك',
    noOrders: 'لا توجد طلبات',
    noOrdersDesc: 'لم تقم بأي طلبات بعد',
    orderPlaced: 'تم تقديم الطلب بنجاح!',
    orderPlacedDesc: 'شكراً لتسوقك معنا. ستصلك رسالة تأكيد قريباً.',
    backToOrders: 'العودة للطلبات',
    orderItems: 'محتويات الطلب',
    orderDate: 'تاريخ الطلب',
    viewDetails: 'عرض التفاصيل',

    // Status
    pending: 'قيد الانتظار',
    processing: 'قيد المعالجة',
    shipped: 'تم الشحن',
    delivered: 'تم التوصيل',
    cancelled: 'ملغي',
    paid: 'مدفوع',

    // Auth
    welcomeBack: 'مرحباً بعودتك',
    signInAccount: 'سجل دخول لحسابك',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    signIn: 'تسجيل الدخول',
    signingIn: 'جاري تسجيل الدخول...',
    noAccount: 'ليس لديك حساب؟',
    createOne: 'أنشئ حساب',
    createAccount: 'إنشاء حساب',
    joinUs: 'انضم إلينا وابدأ التسوق',
    name: 'الاسم',
    confirmPassword: 'تأكيد كلمة المرور',
    creatingAccount: 'جاري إنشاء الحساب...',
    haveAccount: 'لديك حساب؟',
    demoAccounts: 'حسابات تجريبية',
    customer: 'عميل',
    admin: 'مدير',
    forgotPassword: 'نسيت كلمة المرور؟',
    resetPassword: 'إعادة تعيين كلمة المرور',
    resetPasswordDesc: 'أدخل بريدك الإلكتروني لإعادة تعيين كلمة المرور',
    sendResetLink: 'إرسال رابط إعادة التعيين',
    sending: 'جاري الإرسال...',
    resetLinkSent: 'تم إرسال رابط إعادة التعيين!',
    resetLinkSentDesc: 'تحقق من بريدك الإلكتروني للحصول على رابط إعادة التعيين',
    backToLogin: 'العودة لتسجيل الدخول',
    enterNewPassword: 'أدخل كلمة المرور الجديدة',
    resetPasswordSuccess: 'تم إعادة تعيين كلمة المرور بنجاح!',
    invalidResetToken: 'رابط إعادة التعيين غير صالح أو منتهي الصلاحية',
    copyResetLink: 'نسخ رابط إعادة التعيين',
    resetLinkCopied: 'تم نسخ الرابط!',
    userNotFound: 'البريد الإلكتروني غير مسجل',

    // Profile
    myProfile: 'ملفي الشخصي',
    manageAccount: 'إدارة إعدادات حسابك',
    profileInfo: 'معلومات الملف الشخصي',
    saveChanges: 'حفظ التغييرات',
    saving: 'جاري الحفظ...',
    changePassword: 'تغيير كلمة المرور',
    currentPassword: 'كلمة المرور الحالية',
    newPassword: 'كلمة المرور الجديدة',
    confirmNewPassword: 'تأكيد كلمة المرور الجديدة',
    changing: 'جاري التغيير...',
    accountInfo: 'معلومات الحساب',
    accountType: 'نوع الحساب',
    administrator: 'مدير',
    profileUpdated: 'تم تحديث الملف الشخصي بنجاح!',
    updateFailed: 'فشل التحديث',
    passwordsDoNotMatch: 'كلمات المرور غير متطابقة',
    passwordMinLength: 'يجب أن تكون كلمة المرور 6 أحرف على الأقل',
    passwordChanged: 'تم تغيير كلمة المرور بنجاح!',
    passwordChangeFailed: 'فشل تغيير كلمة المرور',

    // Footer
    quickLinks: 'روابط سريعة',
    account: 'الحساب',
    contact: 'تواصل معنا',
    allRightsReserved: 'جميع الحقوق محفوظة',

    // Admin
    dashboard: 'لوحة التحكم',
    manageProducts: 'إدارة المنتجات',
    viewOrders: 'عرض الطلبات',
    viewStore: 'عرض المتجر',
    totalOrders: 'إجمالي الطلبات',
    totalRevenue: 'إجمالي الإيرادات',
    pendingOrders: 'طلبات معلقة',
    completedOrders: 'طلبات مكتملة',
    recentOrders: 'الطلبات الأخيرة',
    quickActions: 'إجراءات سريعة',
    addProduct: 'إضافة منتج',
    editProduct: 'تعديل المنتج',
    deleteProduct: 'حذف المنتج',
    productName: 'اسم المنتج',
    price: 'السعر',
    salePrice: 'سعر التخفيض',
    originalPrice: 'السعر الأصلي',
    onSale: 'تخفيض',
    sale: 'تخفيض',
    youSave: 'توفر',
    promoCode: 'رمز الخصم',
    applyCode: 'تطبيق',
    discount: 'الخصم',
    promoApplied: 'تم تطبيق رمز الخصم',
    invalidCode: 'رمز الخصم غير صالح',
    expiredCode: 'رمز الخصم منتهي الصلاحية',
    codeMinOrder: 'الحد الأدنى للطلب',
    removeCode: 'إزالة',
    stock: 'المخزون',
    actions: 'الإجراءات',
    imageUrl: 'رابط الصورة',
    featuredProduct: 'منتج مميز',
    cancel: 'إلغاء',
    update: 'تحديث',
    add: 'إضافة',
    confirmDelete: 'هل أنت متأكد من حذف هذا المنتج؟',
    items: 'منتجات',
    updateStatus: 'تحديث الحالة',
    manageOrders: 'إدارة الطلبات',
    orderNumber: 'رقم الطلب',
    orderId: 'رقم الطلب',
    date: 'التاريخ',
    all: 'الكل',
    noOrdersFound: 'لا توجد طلبات',

    // Emirates
    abuDhabi: 'أبوظبي',
    dubai: 'دبي',
    sharjah: 'الشارقة',
    ajman: 'عجمان',
    umm_al_quwain: 'أم القيوين',
    ras_al_khaimah: 'رأس الخيمة',
    fujairah: 'الفجيرة',

    // Currency
    currency: 'درهم',
    aed: 'درهم',

    // Admin Order Detail
    orderNotFound: 'الطلب غير موجود',
    backToOrdersList: '← العودة للطلبات',
    customerInfo: 'العميل',
    orderHistory: 'سجل الطلبات',
    copy: 'نسخ',
    copied: 'تم النسخ!',
    print: 'طباعة',
    save: 'حفظ',
    phoneNumber: 'رقم الهاتف',
    emailAddress: 'البريد الإلكتروني',
    orderCount: 'طلب',
    ordersCount: 'طلبات',
    memberSince: 'عضو منذ',
    creditDebitCard: 'بطاقة ائتمان / خصم',
    bankTransfer: 'تحويل بنكي',
    cashOnDeliveryFull: 'الدفع عند الاستلام',
    pendingVerification: 'في انتظار التحقق',
    verified: 'تم التحقق',
    collected: 'تم التحصيل',
    refunded: 'مسترد',
    partiallyRefunded: 'مسترد جزئياً',
    failed: 'فشل',
    checkRefundStatus: '🔄 التحقق من حالة الاسترداد',
    checking: '⏳ جاري التحقق...',
    transferReceipt: 'إيصال التحويل',
    clickToViewFullSize: 'انقر لعرض الحجم الكامل',
    openInNewTab: 'فتح في نافذة جديدة ↗',
    product: 'المنتج',
    qty: 'الكمية',
    lastUpdated: 'آخر تحديث',
    paymentId: 'رقم الدفع',
    markAsShipped: 'تحديد كتم الشحن',
    confirmShipQuestion: 'هل أنت متأكد من تحديد هذا الطلب كتم شحنه؟',
    sendEmailNotification: 'إرسال إشعار بالبريد',
    noEmailAvailable: 'لا يوجد بريد إلكتروني',
    confirmShip: 'تأكيد الشحن',
deleteOrder: 'حذف الطلب',
    confirmDeleteOrder: 'هل أنت متأكد من حذف هذا الطلب؟ لا يمكن التراجع عن هذا الإجراء.',
    deleting: 'جاري الحذف...',
    orderDeleted: 'تم حذف الطلب',
    updating: 'جاري التحديث...',
    created: 'تم الإنشاء',
    whatsappMessage: 'سيتم إرسال رسالة للعميل لإتمام الطلب',
    copyLink: '🔗 نسخ الرابط'
  },
  en: {
    // Header
    home: 'Home',
    products: 'Products',
    cart: 'Cart',
    login: 'Login',
    signup: 'Sign Up',
    logout: 'Logout',
    profile: 'Profile',
    myOrders: 'Orders',
    adminDashboard: 'Admin Dashboard',

    // Home
    heroTitle: 'Discover Amazing Products',
    heroSubtitle: 'Shop the latest products with fast delivery across UAE',
    shopNow: 'Shop Now',
    fastDelivery: 'Fast Delivery',
    fastDeliveryDesc: 'All UAE Emirates - AED 30',
    securePayment: 'Secure Payment',
    securePaymentDesc: '100% encrypted transactions',
    easyReturns: 'Easy Returns',
    easyReturnsDesc: '14-day return policy',
    support: '24/7 Support',
    supportDesc: 'Dedicated support team',
    featuredProducts: 'Featured Products',
    viewAll: 'View All',
    newsletter: 'Join Our Newsletter',
    newsletterDesc: 'Get the latest offers and updates',
    subscribe: 'Subscribe',
    enterEmail: 'Enter your email',

    // Products
    allProducts: 'All Products',
    discoverCollection: 'Discover our amazing collection',
    search: 'Search',
    searchProducts: 'Search products...',
    category: 'Category',
    allCategories: 'All Categories',
    priceRange: 'Price Range',
    min: 'Min',
    max: 'Max',
    clearFilters: 'Clear Filters',
    productsFound: 'products',
    noProducts: 'No products found',
    addToCart: 'Add to Cart',
    featured: 'Featured',

    // Product Detail
    description: 'Description',
    inStock: 'In Stock',
    outOfStock: 'Out of Stock',
    available: 'available',
    quantity: 'Quantity',
    addedToCart: 'Added to Cart',
    deliveryNote: 'Delivery to all UAE Emirates - AED 30',
    glutenFree: '100% Gluten Free',

    // Cart
    shoppingCart: 'Shopping Cart',
    itemsInCart: 'items in your cart',
    emptyCart: 'Your Cart is Empty',
    emptyCartDesc: 'You haven\'t added any items yet',
    startShopping: 'Start Shopping',
    orderSummary: 'Order Summary',
    subtotal: 'Subtotal',
    shipping: 'Shipping',
    tax: 'Tax (5% VAT)',
    total: 'Total',
    free: 'FREE',
    proceedToCheckout: 'Proceed to Checkout',
    continueShopping: 'Continue Shopping',

    // Checkout
    checkout: 'Checkout',
    shippingAddress: 'Shipping Address',
    payment: 'Payment',
    fullName: 'Full Name',
    address: 'Address',
    city: 'City',
    emirate: 'Emirate',
    selectEmirate: 'Select Emirate',
    phone: 'Phone Number',
    cashOnDelivery: 'Cash on Delivery',
    placeOrder: 'Place Order',
    orderFailed: 'Failed to create order',
    shippingTo: 'Shipping To',
    edit: 'Edit',
    paymentInfo: 'Payment Information',
    paymentNote: 'Demo: Use card 4242 4242 4242 4242',
    pay: 'Pay',
    processing: 'Processing...',
    paymentMethod: 'Payment Method',
    creditCard: 'Credit / Debit Card',
    cardDetails: 'Card Details',
    orPayWithCard: 'or pay with card',
    payNow: 'Pay Now',
    payWhenReceive: 'Pay when you receive your order',

    // Orders
    orders: 'Orders',
    viewTrackOrders: 'View and track your orders',
    noOrders: 'No Orders Yet',
    noOrdersDesc: 'You haven\'t placed any orders yet',
    orderPlaced: 'Order Placed Successfully!',
    orderPlacedDesc: 'Thank you for shopping with us. You will receive a confirmation email shortly.',
    backToOrders: 'Back to Orders',
    orderItems: 'Order Items',
    orderDate: 'Order Date',
    viewDetails: 'View Details',

    // Status
    pending: 'Pending',
    processing: 'Processing',
    shipped: 'Shipped',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
    paid: 'Paid',

    // Auth
    welcomeBack: 'Welcome Back',
    signInAccount: 'Sign in to your account',
    email: 'Email',
    password: 'Password',
    signIn: 'Sign In',
    signingIn: 'Signing in...',
    noAccount: 'Don\'t have an account?',
    createOne: 'Create one',
    createAccount: 'Create Account',
    joinUs: 'Join us and start shopping',
    name: 'Name',
    confirmPassword: 'Confirm Password',
    creatingAccount: 'Creating Account...',
    haveAccount: 'Already have an account?',
    demoAccounts: 'Demo Accounts',
    customer: 'Customer',
    admin: 'Admin',
    forgotPassword: 'Forgot Password?',
    resetPassword: 'Reset Password',
    resetPasswordDesc: 'Enter your email to reset your password',
    sendResetLink: 'Send Reset Link',
    sending: 'Sending...',
    resetLinkSent: 'Reset Link Sent!',
    resetLinkSentDesc: 'Check your email for the reset link',
    backToLogin: 'Back to Login',
    enterNewPassword: 'Enter your new password',
    resetPasswordSuccess: 'Password reset successfully!',
    invalidResetToken: 'Invalid or expired reset link',
    copyResetLink: 'Copy Reset Link',
    resetLinkCopied: 'Link copied!',
    userNotFound: 'Email not registered',

    // Profile
    myProfile: 'My Profile',
    manageAccount: 'Manage your account settings',
    profileInfo: 'Profile Information',
    saveChanges: 'Save Changes',
    saving: 'Saving...',
    changePassword: 'Change Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmNewPassword: 'Confirm New Password',
    changing: 'Changing...',
    accountInfo: 'Account Information',
    accountType: 'Account Type',
    administrator: 'Administrator',
    profileUpdated: 'Profile updated successfully!',
    updateFailed: 'Update failed',
    passwordsDoNotMatch: 'Passwords do not match',
    passwordMinLength: 'Password must be at least 6 characters',
    passwordChanged: 'Password changed successfully!',
    passwordChangeFailed: 'Password change failed',

    // Footer
    quickLinks: 'Quick Links',
    account: 'Account',
    contact: 'Contact',
    allRightsReserved: 'All rights reserved',

    // Admin
    dashboard: 'Dashboard',
    manageProducts: 'Manage Products',
    viewOrders: 'View Orders',
    viewStore: 'View Store',
    totalOrders: 'Total Orders',
    totalRevenue: 'Total Revenue',
    pendingOrders: 'Pending Orders',
    completedOrders: 'Completed Orders',
    recentOrders: 'Recent Orders',
    quickActions: 'Quick Actions',
    addProduct: 'Add Product',
    editProduct: 'Edit Product',
    deleteProduct: 'Delete Product',
    productName: 'Product Name',
    price: 'Price',
    salePrice: 'Sale Price',
    originalPrice: 'Original Price',
    onSale: 'On Sale',
    sale: 'Sale',
    youSave: 'You save',
    promoCode: 'Promo Code',
    applyCode: 'Apply',
    discount: 'Discount',
    promoApplied: 'Promo code applied',
    invalidCode: 'Invalid promo code',
    expiredCode: 'Promo code expired',
    codeMinOrder: 'Minimum order amount',
    removeCode: 'Remove',
    stock: 'Stock',
    actions: 'Actions',
    imageUrl: 'Image URL',
    featuredProduct: 'Featured Product',
    cancel: 'Cancel',
    update: 'Update',
    add: 'Add',
    confirmDelete: 'Are you sure you want to delete this product?',
    items: 'items',
    updateStatus: 'Update Status',
    manageOrders: 'Manage Orders',
    orderNumber: 'Order Number',
    orderId: 'Order ID',
    date: 'Date',
    all: 'All',
    noOrdersFound: 'No orders found',

    // Emirates
    abuDhabi: 'Abu Dhabi',
    dubai: 'Dubai',
    sharjah: 'Sharjah',
    ajman: 'Ajman',
    umm_al_quwain: 'Umm Al Quwain',
    ras_al_khaimah: 'Ras Al Khaimah',
    fujairah: 'Fujairah',

    // Currency
    currency: 'AED',
    aed: 'AED',

    // Admin Order Detail
    orderNotFound: 'Order not found',
    backToOrdersList: '← Back to Orders',
    customerInfo: 'Customer',
    orderHistory: 'Order History',
    copy: 'Copy',
    copied: 'Copied!',
    print: 'Print',
    save: 'Save',
    phoneNumber: 'Phone',
    emailAddress: 'Email',
    orderCount: 'order',
    ordersCount: 'orders',
    memberSince: 'Since',
    creditDebitCard: 'Credit/Debit Card',
    bankTransfer: 'Bank Transfer',
    cashOnDeliveryFull: 'Cash on Delivery',
    pendingVerification: 'Pending Verification',
    verified: 'Verified',
    collected: 'Collected',
    refunded: 'Refunded',
    partiallyRefunded: 'Partially Refunded',
    failed: 'Failed',
    checkRefundStatus: '🔄 Check Refund Status',
    checking: '⏳ Checking...',
    transferReceipt: 'Transfer Receipt',
    clickToViewFullSize: 'Click to view full size',
    openInNewTab: 'Open in new tab ↗',
    product: 'Product',
    qty: 'Qty',
    lastUpdated: 'Last updated',
    paymentId: 'Payment ID',
    markAsShipped: 'Mark as Shipped',
    confirmShipQuestion: 'Are you sure you want to mark this order as shipped?',
    sendEmailNotification: 'Send email notification',
    noEmailAvailable: 'No email available',
    confirmShip: 'Confirm Ship',
deleteOrder: 'Delete Order',
    confirmDeleteOrder: 'Are you sure you want to delete this order? This action cannot be undone.',
    deleting: 'Deleting...',
    orderDeleted: 'Order deleted',
    updating: 'Updating...',
    created: 'Created',
    whatsappMessage: 'Will send a message asking customer to complete the order',
    copyLink: '🔗 Copy Link'
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'ar';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  const t = (key) => {
    return translations[language][key] || translations['en'][key] || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'ar' ? 'en' : 'ar');
  };

  const isRTL = language === 'ar';

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
    isRTL
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
